<template>
  <div
    class="calendar-wrapper"
    :class="{
      'rounded-lg shadow border border-gray-200 bg-white p-4': !$useNewLayout,
      '-mx-8 use-new-layout': $useNewLayout
    }"
  >
    <FullCalendar
      v-if="fullcalendarKey"
      :key="fullcalendarKey"
      ref="calendarRef"
      :options="calendarOptions"
      :class="{
        'base-calendar': !isScheduler,
        'base-scheduler': isScheduler,
      }"
    >
      <template #eventContent="{ event }">
        <slot
          name="eventContent"
          :event="event"
        />
      </template>
      <template #resourceLabelContent="{ resource }">
        <slot
          name="resourceLabelContent"
          :resource="resource"
        />
      </template>
    </FullCalendar>
  </div>
</template>
<script>
import '@fullcalendar/core/vdom' // solve problem with Vite
import FullCalendar from '@fullcalendar/vue3'
import rrulePlugin from '@fullcalendar/rrule/main.cjs.js'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin from '@fullcalendar/interaction'
import resourceTimelinePlugin from '@fullcalendar/resource-timeline';
import scrollGridPlugin from '@fullcalendar/scrollgrid';
import { decodeFilters } from "@/modules/common/utils/filterUtils";
import differenceInDays from "date-fns/differenceInDays";
import addDays from "date-fns/addDays";
import { getDateWithoutTimezone } from "@/modules/tasks/utils/formModelUtils";
import { convertDateRangeToAdvancedFilter } from "@/modules/users/util/filters";
import { getWeekDays } from "@/modules/common/utils/dateUtils";
import { fcLocales } from "@/i18n";


const LicenseKey = import.meta.env.VITE_FULL_CALENDAR_LICENSE_KEY

export default {
  components: {
    FullCalendar,
  },
  props: {
    isScheduler: {
      type: Boolean,
      default: false,
    },
    options: {
      type: Object,
      default: () => ({}),
    },
    schedulerOptions: {
      type: Object,
      default: () => ({}),
    },
    entity: {
      type: String,
      default: 'tasks'
    },
    loading: {
      type: Boolean,
      default: false,
    },
    canSelect: {
      type: Boolean,
      default: true,
    },
    canResize: {
      type: Boolean,
      default: true,
    },
    canMove: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      fullcalendarKey: 0,
      calendarApi: null,
    }
  },
  computed: {
    isGroupedByAllocations() {
      return this.groupByProp?.includes('allocations')
    },
    isGroupedByStatus() {
      return this.groupByColumn?.prop?.includes('status')
    },
    isEventResourceEditable() {
      return this.isGroupedByStatus || this.isGroupedByAllocations
    },
    groupByColumn() {
      return this.$store.getters['filters/targetLocalFilters'](this.entity)?.groupBy || {}
    },
    groupByProp() {
      let groupColumnProp = this.groupByColumn?.prop || ''
      let groupPropParts = groupColumnProp?.split('.') || []
      return groupPropParts[groupPropParts.length - 1]
    },
    dateColumn() {
      let dateColumn = 'date'
      if (this.entity === 'project') {
        dateColumn = 'start_date_deadline'
      }
      return dateColumn
    },
    startEndDates() {
      if (!this.$route.query.filters) {
        return {
          start: undefined,
          end: undefined
        }
      }

      const decodedFilters = decodeFilters(this.$route.query.filters)

      const filterByFilter = decodedFilters.find(x => x.key === 'filters');

      if (!filterByFilter) {
        return {
          start: undefined,
          end: undefined
        }
      }

      const dateFilter = filterByFilter.value.find(x => x.column === this.dateColumn);
      
      if (!dateFilter) {
        return {
          start: undefined,
          end: undefined
        }
      }

      return {
        start: dateFilter?.query?.min,
        end: dateFilter?.query?.max
      }
    },
    defaultViewType() {
      if (this.isScheduler) {
        return 'resourceTimelineWeek'
      }

      return 'dayGridMonth'
    },
    defaultSlotMinWidth() {
      return 200
    },
    schedulerOptionsComputed() {
      if (!this.isScheduler) {
        return {}
      }
      
      const minSlotWidth = 50
      const maxSlotWidth = 500
      const zoomStep = 50

      return {
        initialView: this.defaultViewType,
        resourceOrder: 'name',
        slotMinWidth: this.defaultSlotMinWidth,
        slotDuration: {
          days: 1,
        },
        slotLabelFormat: [
          { month: 'long', year: 'numeric' }, // top level of text
          { weekday: 'short', day: 'numeric' } // lower level of text
        ],
        headerToolbar: {
          right: 'zoomOut resetZoom zoomIn today prev,next',
          center: 'title',
          left: 'resourceTimelineDay,resourceTimelineWeek,resourceTimelineMonth,resourceTimeline2Months',
        },
        views: {
          resourceTimeline2Months: {
            type: 'resourceTimeline',
            duration: { months: 2 },
            buttonText: '2 months'
          }
        },
        customButtons: {
          zoomOut: {
            text: '-',
            click: () => {
              const current =  this.$route.query?.slotWidth || this.defaultSlotMinWidth
              const newValue = Number(current) - zoomStep

              if (newValue < minSlotWidth) {
                return
              }

              this.$router.replace({
                path: this.$route.path,
                query: {
                  ...this.$route.query,
                  slotWidth: newValue
                }
              })
            }
          },
          resetZoom: {
            text: this.$t('Reset'),
            click: () => {
              this.$router.replace({
                path: this.$route.path,
                query: {
                  ...this.$route.query,
                  slotWidth: this.defaultSlotMinWidth
                }
              })
            }
          },
          zoomIn: {
            text: '+',
            click: () => {
              const current = this.$route.query?.slotWidth || this.defaultSlotMinWidth
              const newValue = Number(current) + zoomStep

              if (newValue > maxSlotWidth) {
                return
              }

              this.$router.replace({
                path: this.$route.path,
                query: {
                  ...this.$route.query,
                  slotWidth: newValue
                }
              })
            }
          }
        },
        contentHeight: null,
        expandRows: true,
        ...this.schedulerOptions,
      }
    },
    calendarOptions() {
      const weekStartDay = +this.$settings('week_start_day')
      const firstDay = weekStartDay ? weekStartDay : 0
      let initialDate = new Date()
      let { end, start } = this.startEndDates

      let initialView = this.defaultViewType
      if (initialView?.includes('resource')) {
        initialView = this.defaultViewType
      }
      try {
        if (start && end) {
          start = new Date(start)
          end = new Date(end)

          if (!this.isScheduler) {
            const difference = differenceInDays(end, start)
            start = addDays(start, difference / 2)
          }

          initialDate = new Date(start)
        }
      } catch (err) {
        console.log('Error parsing the calendar start date')
      }
      
      const select = this.canSelect ? this.handleDateSelect : undefined
      const eventClick = this.handleEventClick
      const eventDrop = this.canMove ? this.handleEventDrop : undefined
      const eventResize = this.canResize ? this.handleEventResize : undefined
      return {
        height: '100%',
        contentHeight: 'auto',
        locales: [
          fcLocales.en,
          fcLocales.es,
          fcLocales.fr,
        ],
        locale: this.$i18n.locale,
        plugins: [
          rrulePlugin,
          dayGridPlugin,
          timeGridPlugin,
          interactionPlugin, // needed for dateClick
          scrollGridPlugin,
          resourceTimelinePlugin,
        ],
        headerToolbar: {
          left: 'dayGridMonth,timeGridWeek,timeGridDay',
          right: 'today prev,next',
          center: 'title',
        },
        initialDate,
        initialView,
        editable: this.canMove,
        resourceEditable: false,
        scrollTimeReset: false,
        eventResourceEditable: this.isEventResourceEditable,
        selectable: this.canSelect,
        resizable: this.canResize,
        selectMirror: true,
        dayMaxEvents: false,
        weekends: true,
        firstDay: firstDay,
        datesSet: this.handleDateChanges,
        dayCellClassNames: this.dayCellClassNames,
        slotLabelClassNames: this.dayCellClassNames,
        slotLaneClassNames: this.dayCellClassNames,
        select,
        eventClick,
        eventDrop,
        eventResize,
        resourceAreaWidth: '250px',
        slotMinWidth: 190,
        aspectRatio: 2,
        schedulerLicenseKey: LicenseKey,
        ...this.options,
        ...this.schedulerOptionsComputed,
      }
    }
  },
  methods: {
    async triggerCalendarReset() {
      this.fullcalendarKey++
      
      await this.$nextTick()
      this.calendarApi = this.$refs.calendarRef?.getApi()


      this.calendarApi?.changeView(this.$route.query?.viewType || this.defaultViewType)
      this.calendarApi?.setOption('slotMinWidth', this.$route.query?.slotWidth || this.defaultSlotMinWidth)
    },
    dayCellClassNames(params) {
      if (this.isDisabledDay(params.date)) {
        return ['bg-gray-50']
      }
      return []
    },
    isDisabledDay(date) {
      const workingDays = this.$settings('working_days') || getWeekDays().map(d => d.value)
      // Sunday is saved as 7 under localisation working days and here returned as 0
      const day = date?.getDay() || 7

      return !workingDays.includes(day)
    },
    handleEventClick(event) {
      this.$emit('event-click', event)
    },
    handleEventDrop(event) {
      this.$emit('event-drop', event)
    },
    handleEventResize(event) {
      this.$emit('event-resize', event)
    },
    async handleDateChanges(event) {
      let dateFormat = 'yyyy-MM-dd'
      let start = this.$formatDate(getDateWithoutTimezone(event.start), dateFormat)
      let end = this.$formatDate(getDateWithoutTimezone(event.end), dateFormat)

      let changedFilters = {
        filters: this.$route.query.filters,
        start,
        end
      }
      
      convertDateRangeToAdvancedFilter(changedFilters, this.dateColumn)
      
      await this.$router.replace({
        path: this.$route.path,
        query: {
          ...this.$route.query,
          filters: changedFilters.filters,
          viewType: event?.view?.type,
        }
      })
    },
    handleDateSelect(selectInfo) {
      let calendarApi = selectInfo.view.calendar
      calendarApi.unselect() // clear date selection
      let daysDifference = differenceInDays(selectInfo.end, selectInfo.start)

      let end = selectInfo.end
      if (end) {
        end.setDate(end.getDate() - 1)
        end = this.$formatDate(end, 'yyyy-MM-dd')
      }
      
      let start_date = selectInfo.startStr
      let end_date = daysDifference > 1 && end ? end : undefined

      this.$emit('select', {
        start_date,
        end_date,
        event: selectInfo,
      })
    },

  },
  watch: {
    '$route.query.viewType': {
      handler(value) {
        if (this.isScheduler && !this.$route?.path?.includes('scheduler')) {
          return
        }
        if (!this.isScheduler && !this.$route?.path?.includes('calendar')) {
          return
        }

        this.calendarApi?.changeView(value || this.defaultViewType)
      },
    },
    '$route.query.slotWidth': {
      handler(value) {
        if (this.isScheduler && !this.$route?.path?.includes('scheduler')) {
          return
        }
        if (!this.isScheduler && !this.$route?.path?.includes('calendar')) {
          return
        }

        this.calendarApi?.setOption('slotMinWidth', value || this.defaultSlotMinWidth)
      },
    },
  },
  mounted() {
    this.triggerCalendarReset()
    this.$store.commit('setIsCalendarView', true)
  },
  unmounted() {
    this.$store.commit('setIsCalendarView', false)
  }
}
</script>
<style lang="scss">
.calendar-task {
  @apply px-2;
}

.calendar-wrapper {
  .base-scheduler {
    min-height: calc(100vh - 260px);
    margin-bottom: 20px;
  }

  .fc-timeline-event {
    @apply rounded;
  }

  &.use-new-layout {
    .base-scheduler {
      min-height: calc(100vh - 165px);
      @apply mb-0;
    }

    .base-calendar {
      max-height: calc(100vh - 165px);
      @apply mb-0 overflow-y-auto;
    }
    
    .fc-header-toolbar {
      @apply px-8 bg-gray-100 py-4 mb-0;
    }
  }
}

.base-scheduler,
.base-calendar {
  --fc-button-bg-color: theme('colors.gray.500');
  --fc-button-border-color: theme('colors.gray.500');
  --fc-button-hover-bg-color: theme('colors.gray.700');
  --fc-button-hover-border-color: theme('colors.gray.700');
  --fc-button-active-bg-color: theme('colors.gray.600');
  --fc-button-active-border-color: theme('colors.gray.600');

  --fc-border-color: theme('colors.gray.200');
}

</style>
